import { Typography } from "@50y/celestial";
import { cx } from "class-variance-authority";
import { useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowDropDownIcon } from "src/assets/icons/general/ArrowDropDown";
import { ActionEvents } from "src/config/amplitudeEvents";
import { useAmplitudeTrack, useWindowSize } from "src/hooks";
import { ScreenBreakpoints } from "src/utils/constants";
import { ActiveSectionContext } from "./context";

export interface ITableOfContents {
  title: string;
  id: string;
  item?: string;
}

const twStyles = {
  tableOfContents:
    "border-l-[1px] py-8 px-6 border-[#E5E5E5] w-toc overflow-y-auto h-vh",
  styledItemClass:
    "cursor-pointer font-bold p-3 border-l-4 border-[transparent] hover:border-interactive-text-text-b-primary hover:text-interactive-text-text-b-primary",
};

export const TableOfContents = ({
  headingIds = [],
}: {
  headingIds: ITableOfContents[] | undefined;
}) => {
  const { track } = useAmplitudeTrack();
  const { activeSection } = useContext(ActiveSectionContext);
  const { pathname } = useLocation();
  const { windowSize } = useWindowSize();
  const [isExpanded, setIsExpanded] = useState(false);

  const isMobile = windowSize.width < ScreenBreakpoints.TABLET;

  return (
    <div
      className={cx(
        twStyles.tableOfContents,
        isMobile && "border-t-[1px] border-l-0 w-full py-4 px-4",
      )}
    >
      <div
        className={cx(
          "flex items-center",
          isMobile && "justify-between cursor-pointer",
        )}
        onClick={() => isMobile && setIsExpanded(!isExpanded)}
      >
        <Typography variant="prominent-1" size="label-3">
          Table of contents
        </Typography>
        {isMobile && (
          <div
            className={`transform transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          >
            <ArrowDropDownIcon width="14" height="8" />
          </div>
        )}
      </div>
      <div
        className={cx("grid gap-3 mt-6", isMobile && !isExpanded && "hidden")}
      >
        {headingIds?.map(({ id, title }) => (
          <div
            key={id}
            onClick={() => {
              const selectedElement = document.getElementById(id);
              selectedElement?.scrollIntoView({ block: "start" });
              track(ActionEvents.CLICKED_RESOURCE_PAGE_HEADING, {
                page: pathname,
                heading: title,
              });
              if (isMobile) {
                setIsExpanded(false);
              }
            }}
          >
            <Typography
              size="body-4"
              variant="default"
              className={cx(
                activeSection === id &&
                  "text-interactive-text-text-b-primary font-headings-prominent-1",
                "cursor-pointer",
              )}
            >
              {title}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};
